import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Carousel,
  Col,
  Flex,
  Input,
  Modal,
  Result,
  Row,
  Spin,
  Table,
  Tour,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useCameras from "../../hooks/useCameras";
import useZonesConfig from "../../hooks/useZonesConfig";
import { VIBRANT_COLORS } from "../../utils/constants";
import {
  checkProximity,
  checkProximityWithOtherZones,
  checkZoneCrossOther,
  drawArrow,
  drawCoordinates,
  drawLineCoordinates,
  formatCoordinates,
  handleNewLine,
} from "../../utils/functions/drawFunctions.js";
import {
  handleCancelMoveVertice,
  handleEditLine,
  handleEditZone,
  handleMoveVertice,
  handleMoveVerticeClick,
} from "../../utils/functions/editFunctions";
import { formatToPost } from "../../utils/functions/postFunctions";
import { handleRemoveItem } from "../../utils/functions/removeFunctions";
import {
  generateCanvaImage,
  updateTable,
} from "../../utils/functions/updateFunctions";
import useApi from "../../utils/useApi";
import CameraModal from "./CameraModal.js";
import ZonesManager from "./ZonesManager";

const { Title, Text } = Typography;

function EditZones({ camerasToEdit, drawMode, existingConfigId }) {
  const { postZones, postLines, updateZonesInCameras, updateLinesInCameras } =
    useApi();

  const { data: allCameras, isLoading: isLoadingCameras } = useCameras();
  const { data: zonesConfig, isLoading: isLoadingConfig } = useZonesConfig();

  const canvasRef = useRef(null);
  const carouselRef = useRef(null);

  const [listCameras, setListCameras] = useState([]);
  const [indexListCameras, setIndexListCameras] = useState(0);

  // coordenatior useStates
  const [cameras, setCameras] = useState({});
  const [zones, setZones] = useState([]);
  const [lines, setLines] = useState([]);

  const [currentCamera, setCurrentCamera] = useState({});
  const [currentColor, setCurrentColor] = useState();

  const [id, setId] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);

  const [firstClick, setFirstClick] = useState({});
  const [currentClick, setCurrentClick] = useState({});

  const [canvas, setCanvas] = useState(null);

  const [isPressed, setIsPressed] = useState(false);

  // Control data exibition
  const [tableData, setTableData] = useState([]);
  const [endEdition, setEndEdition] = useState(false);
  const [sucessEdition, setSucessEdition] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);

  // Save user preferences
  const [savedCanvas, setSavedCanvas] = useState([]);
  const [zoneDescription, setZoneDescription] = useState("");
  const zoneDescriptionRef = useRef(zoneDescription);
  const [lineDescription, setLineDescription] = useState("");
  const lineDescriptionRef = useRef(lineDescription);
  const [zoneCollection, setZoneCollection] = useState("");
  const zoneCollectionRef = useRef(zoneCollection);

  // Edit zone
  const [editMode, setEditMode] = useState(false);
  const [zoneBeingEdited, setZoneBeingEdited] = useState(-1);
  const [lineBeingEdited, setLineBeingEdited] = useState(-1);
  const [coordIndex, setCoordIndex] = useState([]);
  const [pointIndex, setPointIndex] = useState([]);
  const [backupCoord, setBackupCoord] = useState([]);
  const [cancelEdit, setCancelEdit] = useState(false);

  const [configId, setConfigId] = useState(0);
  const [addLineZone, setAddLineZone] = useState("");

  const [currentImageUrl, setCurrentImageUrl] = useState(0);
  const drawZonesRef = useRef(null);
  const [isEndEdition, setIsEndEdition] = useState(false);

  const isLastCamera = indexListCameras + 1 === listCameras.length;

  /* eslint-disable-next-line no-unused-vars */
  const [zonestoPost, setZonestoPost] = useState([]);

  const [isSixthPhaseTutorialOpen, setIsSixthPhaseTutorialOpen] =
    useState(false);

  const { shouldShowTutorialBool } = useAuth();
  const [isFifthPhaseTutorialOpen, setIsFifthPhaseTutorialOpen] =
    useState(false);

  const [isEditingLoading, setIsEditingLoading] = useState(false);
  const [isCancellingEdit, setIsCancellingEdit] = useState(false);

  // Post data
  const [postCameras, setPostCameras] = useState({});
  const steps = [
    {
      title: "11º passo: Desenhe as zonas",
      description:
        "Desenhe as zonas ou linhas nas câmeras selecionadas, quando chegar a última câmera ou quiser finalizar a edição clique em 'Finalizar edição'.",
      target: () => drawZonesRef.current,
      nextButtonProps: {
        children: "Ok",
      },
    },
    {
      title: "12º passo: Adicione uma categoria",
      description:
        "Na barra lateral clique em 'Gerenciar categorias' para acessar o menu de categorias e vincular uma zona à uma categoria",
      target: () => drawZonesRef.current,
      nextButtonProps: {
        children: "Ok",
      },
    },
  ];

  const apiRef = useRef({});

  const camerasToEditRef = useRef(camerasToEdit);

  useEffect(() => {
    camerasToEditRef.current = camerasToEdit;
  }, [camerasToEdit]);

  useEffect(() => {
    apiRef.current = {
      postZones,
      postLines,
      updateZonesInCameras,
      updateLinesInCameras,
    };
  }, [postZones, postLines, updateZonesInCameras, updateLinesInCameras]);

  useEffect(() => {
    const resizeImage = (base64Str, newWidth) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = base64Str;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const aspectRatio = img.height / img.width;
          const newHeight = newWidth * aspectRatio;

          canvas.width = newWidth;
          canvas.height = newHeight;

          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          const resizedBase64 = canvas.toDataURL("image/jpeg");
          resolve({ resizedBase64, newWidth, newHeight });
        };
        img.onerror = () => {
          console.error("Erro ao carregar a imagem da câmera para edição.");
          resolve(null);
        };
      });
    };

    const loadInitialData = async () => {
      if (!allCameras || !zonesConfig) return;

      try {
        const cameras_response = allCameras
          .filter((camera) => camerasToEditRef.current.includes(camera.id))
          .filter((camera) => camera.image)
          .map((camera) => {
            const base64Image = `data:image/jpeg;base64,${camera.image}`;
            return resizeImage(base64Image, 960).then((result) =>
              result
                ? {
                    id: camera.id,
                    image: result.resizedBase64,
                    width: result.newWidth,
                    height: result.newHeight,
                  }
                : null
            );
          });

        const camerasWithDimensions = (
          await Promise.all(cameras_response)
        ).filter(Boolean);
        setListCameras(camerasWithDimensions);
        setCurrentCamera(camerasWithDimensions[0]);
        setCurrentColor(VIBRANT_COLORS[0].code);

        const updatedSavedCanvas = camerasWithDimensions.map((camera) => ({
          id: camera.id,
          imageUrl: camera.image,
        }));
        setSavedCanvas(updatedSavedCanvas);

        const parsedExistingConfigId = Number(existingConfigId);
        const existingConfig = zonesConfig.find(
          (config) => config.id === parsedExistingConfigId
        );

        if (existingConfig) {
          setConfigId(existingConfig.id);
        } else if (zonesConfig.length > 0) {
          const maxId = Math.max(...zonesConfig.map((config) => config.id));
          const maxConfig = zonesConfig.find((config) => config.id === maxId);
          setConfigId(maxConfig.id);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
      }
    };

    loadInitialData();
  }, [allCameras, zonesConfig]);

  window.onbeforeunload = (event) => {
    if (!endEdition) {
      const e = event || window.event;
      e.preventDefault();
      if (e) {
        e.returnValue = "";
      }
      return "";
    }
  };

  const [awaitingArrowSelection, setAwaitingArrowSelection] = useState(false);
  const [lastLineId, setLastLineId] = useState(null);
  const [lastLinePoints, setLastLinePoints] = useState(null);

  const [showDirectionMessage, setShowDirectionMessage] = useState(false);

  // Draw function
  const detectPointPosition = (event) => {
    const index = lines.findIndex((line) => line.id === id);

    const x1 = lines[index].coordinates[0][0].x;
    const y1 = lines[index].coordinates[0][0].y;
    const x2 = lines[index].coordinates[0][1].x;
    const y2 = lines[index].coordinates[0][1].y;

    const rect = canvas.getBoundingClientRect();
    const currentCoordinates = formatCoordinates(
      canvas,
      rect,
      event.clientX,
      event.clientY
    );
    const px = currentCoordinates.x;
    const py = currentCoordinates.y;

    const position = (x2 - x1) * (py - y1) - (y2 - y1) * (px - x1);
    let d = position > 0 ? "acima" : "abaixo";

    const new_lines = [...lines];
    new_lines[index].direction = d;
    setLines(new_lines);

    const context = canvas.getContext("2d");
    context.save();

    context.translate(x1, y1);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    context.rotate(angle);

    const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

    if (d === "acima") {
      context.fillStyle = "#FF00FF";
      context.fillRect(0, 3, length, 50);
      const new_line = [
        [lines[index].coordinates[0][1], lines[index].coordinates[0][0]],
      ];
      new_lines[index].coordinates = new_line;

      setLines(new_lines);
    } else if (d === "abaixo") {
      context.fillStyle = "#FF00FF";
      context.fillRect(0, -53, length, 50);
    }

    context.restore();
    confirm();
  };

  // Handle
  const handleNewLineClick = (event) => {
    const rect = canvas.getBoundingClientRect();
    const currentCoordinates = formatCoordinates(
      canvas,
      rect,
      event.clientX,
      event.clientY
    );

    if (awaitingArrowSelection && lastLineId !== null && lastLinePoints) {
      const { first, second } = lastLinePoints;
      const midX = (first.x + second.x) / 2;
      const midY = (first.y + second.y) / 2;
      const angle = Math.atan2(second.y - first.y, second.x - first.x);
      const arrowLength = 30;
      const clickX = currentCoordinates.x;
      const clickY = currentCoordinates.y;

      const arrow1 = {
        x: midX + Math.cos(angle + Math.PI / 2) * arrowLength,
        y: midY + Math.sin(angle + Math.PI / 2) * arrowLength,
      };
      const arrow2 = {
        x: midX + Math.cos(angle - Math.PI / 2) * arrowLength,
        y: midY + Math.sin(angle - Math.PI / 2) * arrowLength,
      };
      const distToArrow1 = Math.sqrt(
        Math.pow(clickX - arrow1.x, 2) + Math.pow(clickY - arrow1.y, 2)
      );
      const distToArrow2 = Math.sqrt(
        Math.pow(clickX - arrow2.x, 2) + Math.pow(clickY - arrow2.y, 2)
      );
      const new_lines = [...lines];
      const index = new_lines.findIndex((line) => line.id === lastLineId);
      if (distToArrow1 < 25 || distToArrow2 < 25) {
        new_lines[index].selectedArrow = distToArrow1 < distToArrow2 ? 1 : 2;
        setLines(new_lines);
        setAwaitingArrowSelection(false);
        setLastLineId(null);
        setLastLinePoints(null);
        setShowDirectionMessage(false);
        confirm();
      }
      return;
    }

    if (drawMode === "zone") {
      if (Object.keys(firstClick).length === 0) {
        if (checkProximityWithOtherZones(currentCoordinates, zones, id)) {
          console.warn("Zone is too close to other zones");
        }
        setFirstClick(currentCoordinates);
        setCurrentClick(currentCoordinates);
        setZones([...zones, { id: id, coordinates: [], color: currentColor }]);
      } else {
        if (
          checkProximity(
            firstClick.x,
            firstClick.y,
            currentCoordinates.x,
            currentCoordinates.y
          )
        ) {
          const new_line = [currentClick, firstClick];

          const index = zones.findIndex((zone) => zone.id === id);
          const new_zones = [...zones];
          if (checkProximityWithOtherZones(currentCoordinates, new_zones, id)) {
            console.warn("Zone is too close to other zones");
          }
          new_zones[index].coordinates.push(new_line);
          setZones(new_zones);

          if (checkZoneCrossOther(new_zones[index], new_zones)) {
            alert("Zona cruzando outra zona!");
          } else {
            confirm();
          }

          setFirstClick({});
          setCurrentClick({});
          return;
        } else {
          const new_line = [currentClick, currentCoordinates];

          const index = zones.findIndex((zone) => zone.id === id);
          const new_zones = [...zones];

          if (checkProximityWithOtherZones(currentCoordinates, new_zones, id)) {
            console.warn("Zone is too close to other zones");
          }

          new_zones[index].coordinates.push(new_line);
          setZones(new_zones);

          setCurrentClick(currentCoordinates);
        }
      }
    } else if (drawMode === "line") {
      if (awaitingArrowSelection) {
        return;
      }
      if (Object.keys(firstClick).length === 0) {
        setFirstClick(currentCoordinates);
        setCurrentClick(currentCoordinates);
        setLines([
          ...lines,
          {
            id: id,
            coordinates: [],
            color: currentColor,
            direction: "",
            selectedArrow: null,
          },
        ]);
      } else {
        const new_line = [firstClick, currentCoordinates];
        const index = lines.findIndex((line) => line.id === id);
        const new_lines = [...lines];
        new_lines[index].coordinates.push(new_line);
        setLines(new_lines);
        setFirstClick({});
        setCurrentClick({});
        setAwaitingArrowSelection(true);
        setLastLineId(id);
        setLastLinePoints({ first: firstClick, second: currentCoordinates });
        setShowDirectionMessage(true);
        return;
      }
    }
  };

  // Final functions
  const postNewZones = (cameras) => {
    return new Promise((resolve, reject) => {
      postZones(cameras)
        .then((response) => {
          const update_cameras_data = response.data;

          updateZonesInCameras(update_cameras_data)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              console.error("Erro ao atualizar zonas nas câmeras:", error);
              reject(error);
            });
        })
        .catch((error) => {
          console.error("Erro ao postar zonas:", error);
          reject(error);
        });
    });
  };

  const postNewLines = (cameras) => {
    return new Promise((resolve, reject) => {
      postLines(cameras)
        .then((response) => {
          const update_cameras_data = response.data;

          updateLinesInCameras(update_cameras_data)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              console.error(error);
              reject(error);
            });
        })
        .catch((error) => {
          console.error(error);
          reject(error);
        });
    });
  };

  // Modals //
  const confirm = () => {
    Modal.confirm({
      title: "Confirmação",
      width: "fit-content",
      okText: "Confirmar",
      cancelText: "Cancelar",
      onOk() {
        updateTable(
          id,
          drawMode === "zone"
            ? zoneDescriptionRef.current
            : lineDescriptionRef.current,
          configId,
          drawMode,
          tableData,
          drawMode === "zone" ? zones : lines,
          setTableData,
          drawMode === "zone" ? setZones : setLines,
          setZoneCollection,
          drawMode === "zone" ? setZoneDescription : setLineDescription,
          setId,
          currentColor,
          setCurrentColor,
          colorIndex,
          setColorIndex,
          canvas,
          savedCanvas,
          camerasToEdit,
          currentCamera,
          editMode,
          drawMode === "zone" ? zoneBeingEdited : lineBeingEdited,
          VIBRANT_COLORS,
          setSavedCanvas
        );
      },
      onCancel() {
        handleRemoveItem(
          id,
          drawMode === "zone" ? zones : lines,
          drawMode === "zone" ? setZones : setLines,
          tableData,
          setTableData,
          canvas,
          editMode,
          drawMode === "zone" ? zoneBeingEdited : lineBeingEdited
        );
      },
      icon: <EditOutlined />,
      align: "center",
      content: (
        <Card>
          <div>
            <Text strong style={{ marginTop: 0, marginBottom: 20 }}>
              {`Escolha uma descrição para a nova ${drawMode === "zone" ? "zona" : "entrada"}`}
            </Text>
            <br />
            <Input
              placeholder="Descrição"
              onChange={(e) => setZoneDescription(e.target.value)}
              style={{ width: "75%" }}
            />
            <br />
            <InfoCircleFilled />
            <Text style={{ marginLeft: 5 }}>
              Deixe em branco para uma descrição padrão
            </Text>
          </div>
        </Card>
      ),
    });
  };

  const alert = (message) => {
    Modal.warning({
      title: "Aviso",
      content: (
        <>
          <Title level={4}>{message}</Title>
          <Text>
            {editMode
              ? "Por favor, edite novamente a zona"
              : "Por favor, desenhe novamente a zona"}
          </Text>
        </>
      ),
      icon: <InfoCircleOutlined />,
      align: "center",
      onOk() {
        editMode
          ? handleCancelMoveVertice(
              setCancelEdit,
              drawMode,
              zones,
              zoneBeingEdited,
              backupCoord,
              setZones,
              lines,
              lineBeingEdited,
              setLines,
              setCoordIndex,
              setPointIndex
            )
          : handleRemoveItem(
              id,
              zones,
              setZones,
              tableData,
              setTableData,
              canvas,
              editMode,
              zoneBeingEdited,
              drawMode
            );
      },
    });
  };

  // --------- UseEffects --------- //

  useEffect(() => {
    if (listCameras.length !== 0) {
      const canvas = canvasRef.current;
      setCanvas(canvas);
    }
  }, [listCameras]);

  useEffect(() => {
    if (canvas) {
      canvas.width = currentCamera.width;
      canvas.height = currentCamera.height;
    }
  }, [currentCamera]);

  useEffect(() => {
    zoneDescriptionRef.current = zoneDescription;
    zoneCollectionRef.current = zoneCollection;
  }, [zoneDescription, zoneCollection]);

  useEffect(() => {
    lineDescriptionRef.current = lineDescription;
  }, [lineDescription]);

  useEffect(() => {
    if (canvas) {
      const context = canvas.getContext("2d");
      {
        drawMode === "zone"
          ? drawCoordinates(context, zones, editMode, zoneBeingEdited, canvas)
          : drawLineCoordinates(
              context,
              lines,
              editMode,
              lineBeingEdited,
              canvas
            );
      }
    }
  }, [editMode]);

  useEffect(() => {
    if (cancelEdit) {
      const context = canvas.getContext("2d");
      drawCoordinates(context, zones, editMode, zoneBeingEdited, canvas);
      setCancelEdit(false);
      setEditMode(false);
      setIsCancellingEdit(false);
    }
  }, [zones]);

  useEffect(() => {
    if (cancelEdit) {
      const context = canvas.getContext("2d");
      drawLineCoordinates(context, lines, editMode, lineBeingEdited, canvas);
      setCancelEdit(false);
      setEditMode(false);
      setIsCancellingEdit(false);
    }
  }, [lines]);
  useEffect(() => {
    if (
      awaitingArrowSelection &&
      lastLinePoints &&
      canvas &&
      lastLineId !== null
    ) {
      const context = canvas.getContext("2d");
      drawLineCoordinates(
        context,
        lines,
        editMode,
        lineBeingEdited,
        canvas,
        true,
        true,
        lastLineId
      );
    }
  }, [
    awaitingArrowSelection,
    lastLinePoints,
    canvas,
    lines,
    editMode,
    lineBeingEdited,
    lastLineId,
  ]);

  // --------- Table Configurations --------- //

  const zones_table_columns = [
    {
      title: "Zona",
      dataIndex: "zone",
      key: "zone",
    },
    {
      title: "Identificação",
      dataIndex: "identification",
      key: "identification",
      render: (text, record) => (
        <Avatar
          shape="square"
          style={{
            width: "30%",
            backgroundColor: zones.find((zone) => zone.id === record.key).color,
          }}></Avatar>
      ),
    },
    {
      title: "Editar",
      dataIndex: "edit",
      key: "edit",
      render: (text, record) => (
        <Button
          onClick={() => {
            handleEditZone(
              record.key,
              setEditMode,
              setZoneBeingEdited,
              zones,
              setBackupCoord
            );
          }}
          icon={<EditOutlined />}></Button>
      ),
    },
    {
      title: "Remover",
      dataIndex: "remove",
      key: "remove",
      render: (text, record) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() =>
            handleRemoveItem(
              record.key,
              zones,
              setZones,
              tableData,
              setTableData,
              canvas,
              editMode,
              zoneBeingEdited,
              drawMode
            )
          }
        />
      ),
    },
  ];

  const line_table_columns = [
    {
      title: "Linha",
      dataIndex: "line",
      key: "line",
    },
    {
      title: "Identificação",
      dataIndex: "identification",
      key: "identification",
      render: (text, record) => (
        <Avatar
          shape="square"
          style={{
            width: "30%",
            backgroundColor: lines.find((line) => line.id === record.key).color,
          }}></Avatar>
      ),
    },
    {
      title: "Editar",
      dataIndex: "edit",
      key: "edit",
      render: (text, record) => (
        <Button
          onClick={() =>
            handleEditLine(
              record.key,
              setEditMode,
              setLineBeingEdited,
              lines,
              setBackupCoord
            )
          }
          icon={<EditOutlined />}></Button>
      ),
    },
    {
      title: "Remover",
      dataIndex: "remove",
      key: "remove",
      render: (text, record) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() =>
            handleRemoveItem(
              record.key,
              lines,
              setLines,
              tableData,
              setTableData,
              canvas,
              editMode,
              lineBeingEdited,
              drawMode
            )
          }
        />
      ),
    },
  ];

  return (
    <>
      {!endEdition ? (
        isLoadingCameras || isLoadingConfig ? (
          <Spin />
        ) : (
          <Card
            title={`Desenhe ${drawMode === "line" ? "a linha" : "as zonas"}`}
            align="center"
            style={{ width: "75%" }}>
            {showDirectionMessage && (
              <Card
                style={{
                  width: "50%",
                  margin: "0 auto 20px auto",
                  border: "1px solid var(--ouran-card-header-bg)",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: 16,
                }}>
                Clique em uma das setas para escolher o sentido de entrada da
                loja. A seta selecionada representa o lado de dentro da loja.
              </Card>
            )}
            <canvas
              ref={canvasRef}
              width={currentCamera.width}
              height={currentCamera.height}
              onClick={!editMode ? handleNewLineClick : null}
              onMouseMove={
                editMode && isPressed
                  ? (event) =>
                      handleMoveVertice(
                        event,
                        canvas,
                        drawMode,
                        zones,
                        zoneBeingEdited,
                        coordIndex,
                        pointIndex,
                        editMode,
                        setZones,
                        lines,
                        lineBeingEdited,
                        setLines,
                        setZonestoPost
                      )
                  : Object.keys(firstClick).length !== 0
                    ? (event) =>
                        handleNewLine(
                          event,
                          canvas,
                          currentClick,
                          currentColor,
                          drawMode,
                          zones,
                          zoneBeingEdited,
                          lines,
                          lineBeingEdited,
                          editMode
                        )
                    : null
              }
              onMouseDownCapture={
                editMode
                  ? (event) =>
                      handleMoveVerticeClick(
                        event,
                        canvas,
                        zones,
                        coordIndex,
                        setCoordIndex,
                        setPointIndex,
                        setIsPressed,
                        drawMode,
                        zoneBeingEdited,
                        lines,
                        lineBeingEdited
                      )
                  : null
              }
              onMouseUpCapture={
                editMode
                  ? (event) =>
                      handleMoveVerticeClick(
                        event,
                        canvas,
                        zones,
                        coordIndex,
                        setCoordIndex,
                        setPointIndex,
                        setIsPressed,
                        drawMode,
                        zoneBeingEdited,
                        lines,
                        lineBeingEdited
                      )
                  : null
              }
              style={{
                backgroundImage: `url(${currentCamera.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            {editMode ? (
              <Flex
                flexDirection="column"
                align="center"
                justify="center"
                style={{
                  width: "100%",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
                gap="50px">
                <Button
                  type="primary"
                  loading={isEditingLoading}
                  onClick={async () => {
                    setIsEditingLoading(true);
                    try {
                      setEditMode(false);
                      {
                        drawMode === "zone"
                          ? setZoneBeingEdited(-1)
                          : setLineBeingEdited(-1);
                      }
                    } finally {
                      setIsEditingLoading(false);
                    }
                  }}>
                  Finalizar edição
                </Button>
                <Button
                  loading={isCancellingEdit}
                  onClick={() => {
                    setIsCancellingEdit(true);
                    handleCancelMoveVertice(
                      setCancelEdit,
                      drawMode,
                      zones,
                      zoneBeingEdited,
                      backupCoord,
                      setZones,
                      lines,
                      lineBeingEdited,
                      setLines,
                      setCoordIndex,
                      setPointIndex
                    );
                  }}>
                  Cancelar edição
                </Button>
              </Flex>
            ) : tableData.length !== 0 ? (
              <>
                <Table
                  columns={
                    drawMode === "line"
                      ? line_table_columns
                      : zones_table_columns
                  }
                  dataSource={tableData}
                  pagination={false}
                  style={{
                    width: "90%",
                    marginBottom: "20px",
                    marginTop: "20px",
                  }}></Table>
              </>
            ) : (
              <></>
            )}
            <Flex
              flexDirection="column"
              align="center"
              justify="center"
              style={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}
              gap="100px">
              <Button
                type="default"
                onClick={() => {
                  setShowModalConfirm(true);
                  const img = generateCanvaImage(
                    canvas,
                    currentCamera,
                    drawMode,
                    drawMode === "zone" ? zones : lines,
                    editMode,
                    drawMode === "zone" ? zoneBeingEdited : lineBeingEdited,
                    false
                  );
                  setCurrentImageUrl(img);
                }}
                disabled={camerasToEdit.length < 2 || isLastCamera || editMode}>
                Próxima câmera
                <ArrowRightOutlined />
              </Button>
            </Flex>
            <CameraModal
              showModalConfirm={showModalConfirm}
              setShowModalConfirm={setShowModalConfirm}
              camerasToEdit={camerasToEdit}
              setCurrentCamera={setCurrentCamera}
              listCameras={listCameras}
              indexListCameras={indexListCameras}
              setIndexListCameras={setIndexListCameras}
              setCameras={setCameras}
              drawMode={drawMode}
              zones={zones}
              lines={lines}
              setZones={setZones}
              setLines={setLines}
              setTableData={setTableData}
              setColorIndex={setColorIndex}
              setCurrentColor={setCurrentColor}
              canvas={canvas}
              cameras={cameras}
              currentCamera={currentCamera}
              VIBRANT_COLORS={VIBRANT_COLORS}
              currentImageUrl={currentImageUrl}
              isEndEdition={isEndEdition}
              isLastCamera={isLastCamera}
              setEndEdition={setEndEdition}
              configId={configId}
              setPostCameras={setPostCameras}
              setShowModal={setShowModal}
              setIsEndEdition={setIsEndEdition}
            />
            <Button
              type="default"
              onClick={() => {
                setIsEndEdition(true);
                setShowModalConfirm(true);
                const img = generateCanvaImage(
                  canvas,
                  currentCamera,
                  drawMode,
                  drawMode === "zone" ? zones : lines,
                  editMode,
                  drawMode === "zone" ? zoneBeingEdited : lineBeingEdited,
                  false
                );
                setCurrentImageUrl(img);
              }}
              style={{
                color: editMode ? "#bfbfbf" : "black",
                border: "1px solid #D9D9D9",
                backgroundColor: editMode ? "#f0f0f0" : "white",
              }}
              onMouseEnter={(e) => {
                if (!editMode) {
                  e.currentTarget.style.backgroundColor = "red";
                  e.currentTarget.style.color = "white";
                }
              }}
              onMouseLeave={(e) => {
                if (!editMode) {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.color = "black";
                }
              }}
              disabled={editMode}>
              Finalizar edição
              <CheckCircleOutlined />
            </Button>

            <Modal
              type="confirm"
              title="Confirme as mudanças!"
              width="fit-content"
              okText="Confirmar"
              cancelText="Cancelar"
              onOk={() => {
                setShowModal(false);
                setEndEdition(true);
                formatToPost(
                  {
                    ...cameras,
                    [currentCamera.id]: drawMode === "zone" ? zones : lines,
                  },
                  drawMode,
                  configId,
                  setPostCameras,
                  listCameras
                );
              }}
              onCancel={() => setShowModal(false)}
              open={showModal}
              align="center">
              <Card align="center">
                Você ainda não passou por todas as câmeras. Deseja continuar?
              </Card>
            </Modal>
          </Card>
        )
      ) : !sucessEdition ? (
        !addLineZone ? (
          <Card
            title={`Confira ${drawMode === "line" ? "a linha desenhada" : "as zonas desenhadas"}`}
            align="center"
            style={{ width: "75%" }}>
            <Carousel infinite={false} ref={carouselRef}>
              {savedCanvas.map((canvas, index) => (
                <>
                  <img
                    key={index}
                    src={canvas?.imageUrl}
                    // Set width and height of the original image
                    style={{ width: "75%" }}
                    alt={`câmera ${index}`}
                  />
                </>
              ))}
            </Carousel>

            <Flex
              justify="space-between"
              style={{ width: "30%", marginTop: "20px" }}>
              <Button
                onClick={() => {
                  carouselRef.current.prev();
                }}>
                <LeftOutlined />
                Anterior
              </Button>
              <Button
                onClick={() => {
                  carouselRef.current.next();
                }}>
                Próximo
                <RightOutlined />
              </Button>
            </Flex>

            <Row
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}>
              <Col>
                <Button
                  type="default"
                  style={{ marginTop: "20px" }}
                  onClick={() => {
                    setAddLineZone(drawMode === "line" ? "zone" : "line");
                    if (drawMode === "line") {
                      postNewLines(postCameras);
                    } else if (drawMode === "zone") {
                      postNewZones(postCameras);
                    }
                  }}
                  disabled={addLineZone}>
                  {`Adicionar uma ${drawMode === "line" ? "zona" : "linha de entrada da loja"} na configuração`}
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={async () => {
                    try {
                      if (drawMode === "zone") {
                        await postNewZones(postCameras);
                      } else {
                        await postNewLines(postCameras);
                      }
                      setSucessEdition(true);
                      setIsSixthPhaseTutorialOpen(true);
                    } catch (error) {
                      console.error("Erro ao postar dados:", error);
                    }
                  }}>
                  Finalizar
                </Button>
              </Col>
            </Row>
          </Card>
        ) : (
          <>
            {addLineZone && (
              <ZonesManager
                addLineZone={addLineZone}
                initialSelectedOption="edit_zone"
                initialConfigState="create"
                existingConfigId={configId}
                initialCamerasToEdit={camerasToEdit}
              />
            )}
          </>
        )
      ) : (
        <Result
          status="success"
          title="Alterações salvas com sucesso!"
          subTitle="As mudanças serão aplicadas ao sistema em, no máximo, 15 minutos."
        />
      )}
      <Tour
        open={shouldShowTutorialBool && isFifthPhaseTutorialOpen}
        steps={steps.slice(0, 1)}
        onClose={() => {
          setIsFifthPhaseTutorialOpen(false);
        }}
        gap={{
          offset: [10, 50],
          radius: 10,
        }}
      />
      <Tour
        open={shouldShowTutorialBool && isSixthPhaseTutorialOpen}
        steps={steps.slice(1, 2)}
        onClose={() => {
          setIsSixthPhaseTutorialOpen(false);
        }}
        gap={{
          offset: [10, 50],
          radius: 10,
        }}
      />
    </>
  );
}

export default EditZones;
